import { Box, Flex } from '@chakra-ui/react';
import { Avatar, Heading, IconButton, TextLink } from 'ui-components';
import { ArrowLeftIcon } from 'ui-components/dist/ArrowLeftIcon';
import { ArrowRightIcon } from 'ui-components/dist/ArrowRightIcon';
import authApi from '~/authApi';
import type { ContentDetailModalTrackIdType } from '~/components/ContentDetailModal';
import { FEATURE_FLAGS, USER_ROLES } from '~/constants';
import { routes } from '~/core/router';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { ISearchContentCreator } from '~/searchApi';
import theme from '~/styles/theme';

import { CONTENT_DETAIL_MODAL_TRACK_ID } from './constants';

interface Props {
  hasNavigation: boolean;
  creator: ISearchContentCreator;
  onNext?: () => void;
  onPrevious?: () => void;
  trackIds: ContentDetailModalTrackIdType;
}

export const Header = ({
  hasNavigation,
  creator,
  onNext,
  onPrevious,
  trackIds,
}: Props) => {
  const foamContentProfileLinkEnabled = useFeature(
    FEATURE_FLAGS.foamContentProfileLink,
  ).isEnabled;

  const { roles } = authApi.user;
  const isCreator =
    roles.includes(USER_ROLES.creator) ||
    roles.includes(USER_ROLES.managed) ||
    roles.includes(USER_ROLES.echoCreator);

  const renderHeading = () => {
    if (!creator?.fullName) {
      return null;
    }

    if (foamContentProfileLinkEnabled && !isCreator) {
      return (
        <Flex gap={2}>
          <Avatar
            size="md"
            alt={creator.fullName}
            sx={{ alignSelf: 'center' }}
            src={creator.avatar}
            track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.creatorName]}
          />
          <TextLink
            href={routes.profileOverview(creator?.slug || '')}
            size="xl"
            variant="primary"
            isBold
            _hover={{
              textDecoration: 'underline',
              textDecorationColor: theme.colors.secondary[500],
            }}
            trackId="foam-view-creator-from-content"
          >
            {creator.fullName}
          </TextLink>
        </Flex>
      );
    }

    return (
      <Flex gap={2} alignItems="center">
        <Avatar
          size="md"
          alt={creator.fullName}
          sx={{ alignSelf: 'center' }}
          src={creator.avatar}
          track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.creatorName]}
        />
        <Heading
          size="sm"
          track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.creatorName]}
        >
          {creator.fullName}
        </Heading>
      </Flex>
    );
  };

  return (
    <Flex
      justifyContent="space-between"
      gap={2}
      w="full"
      marginRight={2}
      alignItems="flex-start"
    >
      {renderHeading()}

      {hasNavigation && (
        <Box>
          <IconButton
            size="sm"
            variant="transparent"
            aria-label="Previous Content Detail"
            icon={<ArrowLeftIcon boxSize={4} />}
            onClick={onPrevious}
            track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.previousButton]}
          />
          <IconButton
            size="sm"
            aria-label="Previous Content Detail"
            variant="transparent"
            icon={<ArrowRightIcon boxSize={4} />}
            onClick={onNext}
            track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.nextButton]}
          />
        </Box>
      )}
    </Flex>
  );
};
